/**
 * formatSlotTime — shared formatter across the entire app.
 * Reads attraction.time_format ('12hr'|'24hr') and attraction.timeslot_display ('start_only'|'start_end').
 *
 * Examples (24hr / start_end): "19:00 – 19:15"
 * Examples (12hr / start_only): "7pm"
 * Examples (12hr / start_end):  "7:00pm – 7:15pm"
 */

function formatOne(time24, mode12) {
  if (!time24) return "";
  const [h, m] = time24.split(":").map(Number);
  const mm = String(m).padStart(2, "0");
  if (mode12) {
    const period = h >= 12 ? "pm" : "am";
    let h12 = h % 12;
    if (h12 === 0) h12 = 12;
    return `${h12}:${mm}${period}`;
  }
  // 24hr
  return `${String(h).padStart(2, "0")}:${mm}`;
}

export function formatSlotTime(start, end, attraction, context = "slot") {
  const tf = attraction?.time_format || "12hr";
  const td = attraction?.timeslot_display || "start_end";
  const mode12 = tf === "12hr";
  const s = formatOne(start, mode12);
  // Open-day open/close hours: ALWAYS show start + end; never affected by start_only setting.
  if (context === "openHours") {
    if (!end) return s;
    return `${s} – ${formatOne(end, mode12)}`;
  }
  // Time slots: honour both the 12/24hr format AND the start_only/start_end display setting.
  if (td === "start_only" || !end) return s;
  return `${s} – ${formatOne(end, mode12)}`;
}

export function formatTime(time24, attraction) {
  const tf = attraction?.time_format || "12hr";
  return formatOne(time24, tf === "12hr");
}

const _MONTHS = ["January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"];
const _DOW = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

function _ordinal(n) {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

function _toDate(input) {
  if (!input) return null;
  if (input instanceof Date) return isNaN(input) ? null : input;
  if (/^\d{4}-\d{2}-\d{2}$/.test(input)) {
    const [y, m, d] = input.split("-").map(Number);
    return new Date(y, m - 1, d);
  }
  const d = new Date(input);
  return isNaN(d) ? null : d;
}

/** "Friday 15th June 2026" (weekday optional). Accepts Date | 'YYYY-MM-DD' | ISO. */
export function formatOrdinalDate(input, { weekday = true } = {}) {
  const d = _toDate(input);
  if (!d) return input ? String(input) : "";
  const wd = weekday ? `${_DOW[d.getDay()]} ` : "";
  return `${wd}${_ordinal(d.getDate())} ${_MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

/** "Friday 15th June 2026 at 3:54pm" — honours the attraction's 12/24hr setting. */
export function formatOrdinalDateTime(input, attraction) {
  const d = _toDate(input);
  if (!d) return input ? String(input) : "";
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${formatOrdinalDate(d, { weekday: true })} at ${formatTime(`${hh}:${mm}`, attraction)}`;
}
